import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Breadcrumb, Loader, Nav, Panel } from 'rsuite';
import { BaseErrorModel, BaseResponseModel, PageState } from '@/models/core.model';
import { AxiosError } from 'axios';
import { toastErrorMessage } from '@/utils/toasterMessage';
import { StoryChapter } from '@/models/story-chapter.model';
import StoryChapterForm from './story-chapter-form';
import { getStoryChapter } from '@/services/story-chapter.service';
import StoryChapterMediaListPage from '../../story-chapter-media/list/story-chapter-media-list';
import StoryChapterTaskListPage from '../../story-chapter-task/list/story-chapter-task-list';
import EncyclopediaRelationList from '@/pages/encyclopedia/relation/encyclopedia-relation-list';
import PageHeader from '@/components/PageHeader';

const CustomNav = ({ active, onSelect, newMode, ...props }) => {
  return (
    <Nav {...props} activeKey={active} onSelect={onSelect}>
      <Nav.Item eventKey="story-chapter-form">Detail</Nav.Item>
      <Nav.Item disabled={newMode} eventKey="story-chapter-medias">Medias</Nav.Item>
      <Nav.Item disabled={newMode} eventKey="story-chapter-tasks">Tasks</Nav.Item>
      <Nav.Item disabled={newMode} eventKey="story-chapter-encyclopedia-items">Encyclopedia Items</Nav.Item>
    </Nav>
  );
};

const Page = () => {

  const storyId = useLocation()?.state?.storyId;
  const requestedTab = useLocation()?.state?.tab;

  const [activeTab, setActiveTab] = useState('story-chapter-form');
  const [formObject, setFormObject] = useState<StoryChapter|undefined>(undefined);
  const [loading, setLoading] = useState(false);

  const nav = useNavigate();

  const { id: idParam } = useParams();
  const id = idParam ? parseInt(idParam, 10) : undefined;
  const pageState = id ? PageState.Edit : PageState.New;


  useEffect(() => {
    if (pageState == PageState.Edit) {
      fetchData();
    } else {
      setFormObject(undefined);
    }
    setActiveTab(pre => requestedTab ?? pre);
  }, [id]);
  
  const fetchData = async () => {
    if (id) {
      setLoading(true);
      try {
        const result: BaseResponseModel<StoryChapter> = await getStoryChapter(id);
        if (result.success) {
          setFormObject(result.result);
        }
      } catch (err) {
        const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
        toastErrorMessage(`Error in fetching Story Chapter: ${errorModel?.errorMessage}`);
        nav('stories');
      }
      finally {
        setLoading(false);
      }
    }
  };
 
  return (
    <Panel
      header={
        <PageHeader title={pageState == PageState.Edit && formObject ? `Edit Chapter: ${formObject.name}` : 'New Chapter'}>
          <Breadcrumb>
            <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
            <Breadcrumb.Item href='/stories'> Stories</Breadcrumb.Item>
            <Breadcrumb.Item href='#' onClick={
                () => storyId || formObject?.storyId ? nav(`/stories/${storyId || formObject?.storyId}`, {state:{tab: 'story-chapters'}}) : nav(`stories/chapters`)
            }> Chapters</Breadcrumb.Item>
            <Breadcrumb.Item active>{pageState == PageState.Edit ? 'Edit' : 'New'}</Breadcrumb.Item>
          </Breadcrumb>
        </PageHeader>
      }
    >
      <CustomNav appearance="subtle" active={activeTab} onSelect={key => setActiveTab(key)} newMode={pageState == PageState.New} />
      {
        loading ? <Loader center content="loading ..." /> :
        <>
          {activeTab == 'story-chapter-form' && <StoryChapterForm formObject={formObject} pageState={pageState} storyId={storyId}/>}
          {activeTab == 'story-chapter-medias' && id && <StoryChapterMediaListPage chapterId={formObject?.id}/>}
          {activeTab == 'story-chapter-tasks' && id && <StoryChapterTaskListPage chapterId={formObject?.id}/>}
          {activeTab == 'story-chapter-encyclopedia-items' && id && <EncyclopediaRelationList storyChapterId={formObject?.id}/>}
        </>
      }
    </Panel>
  );
};

export default Page;
